import React, { useState, useEffect } from "react";
import { Modal, Form, InputNumber, Select, Input, message } from "antd";
import { adminOperationsService } from "../../../services/adminService";
import { FrontDeskBooking } from "../../../types";
import { PAYMENT_METHOD_OPTIONS } from "../../../constants/paymentMethods";

interface DepositPaymentModalProps {
  open: boolean;
  booking: FrontDeskBooking | null;
  onCancel: () => void;
  onSuccess: () => void;
}

export const DepositPaymentModal: React.FC<DepositPaymentModalProps> = ({
  open,
  booking,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const totalBookingAmount = Number(
    (booking as any)?.server_total_price ?? (booking as any)?.total_price ?? 0,
  );
  const depositRequired = Number((booking as any)?.deposit_amount || 0);
  const depositPaid = Number((booking as any)?.deposit_paid || 0);
  const remaining = Math.max(0, depositRequired - depositPaid);
  // No deposit outstanding -> staff may still record any amount up to the booking total
  const maxAmount = remaining > 0 ? remaining : Math.max(totalBookingAmount, 1);

  useEffect(() => {
    if (open && booking) {
      form.setFieldsValue({
        amount: remaining > 0 ? remaining : undefined,
        method: "cash",
      });
    }
  }, [open, booking, form, remaining]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await adminOperationsService.recordPayment({
        booking_id: booking?.id,
        amount: Number(values.amount),
        method: values.method,
        status: "completed",
        transaction_id: values.transaction_id || undefined,
        payment_type: "deposit",
      });

      message.success("Deposit payment recorded successfully");
      form.resetFields();
      onSuccess();
      onCancel();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.response?.data?.message || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Collect Deposit"
      open={open}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="Record Payment"
      width={420}
    >
      {booking && (
        <div style={{ marginBottom: 16, padding: 12, background: "#f5f5f5", borderRadius: 8 }}>
          <div><strong>Guest:</strong> {booking.guest_name}</div>
          {booking.confirmation_number && (
            <div><strong>Ref:</strong> {booking.confirmation_number}</div>
          )}
          <div>
            <strong>Total Booking Amount:</strong> LKR{" "}
            {totalBookingAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </div>
          <div>
            <strong>Deposit Required:</strong> LKR{" "}
            {depositRequired.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </div>
          <div>
            <strong>Already Paid:</strong> LKR{" "}
            {depositPaid.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </div>
          <div style={{ fontWeight: 600, color: "#fa8c16", marginTop: 4 }}>
            Remaining Deposit Due: LKR{" "}
            {remaining.toLocaleString("en-US", { minimumFractionDigits: 2 })}
          </div>
          {depositRequired === 0 && (
            <div style={{ fontSize: 12, color: "#888", marginTop: 6 }}>
              No deposit required for this booking. You can still record a payment of up to LKR{" "}
              {totalBookingAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}.
            </div>
          )}
        </div>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="amount"
          label="Amount"
          rules={[{ required: true, message: "Please enter amount" }]}
        >
          <InputNumber
            min={1}
            max={maxAmount}
            style={{ width: "100%" }}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            parser={(value) => Number(value?.replace(/,/g, "") || 0) as any}
            addonAfter="LKR"
          />
        </Form.Item>

        <Form.Item
          name="method"
          label="Payment Method"
          rules={[{ required: true, message: "Please select payment method" }]}
        >
          <Select options={PAYMENT_METHOD_OPTIONS} />
        </Form.Item>

        <Form.Item name="transaction_id" label="Reference / Transaction ID (Optional)">
          <Input placeholder="Enter reference number" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
